import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {Link} from '../../../components/common/links/link';
import {Pill} from '../../../components/pill/pill';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {CompanyFeaturesEnum} from '../../../models/api/companyModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {getFindHasCompanyFeature} from '../../../store/data/company/companySelectors';
import {RuleRecipe} from './recipes';
import {RuleLibraryCard} from './ruleLibraryCard';
import {UpsellOverlay} from './upsellWrapper';

/*
 * Props.
 */

interface RuleLibraryRecipeCardProps {
  recipe: DisplayableRuleRecipe;
}

/*
 * Constants.
 */

const UPSELL_URL = '/settings/billing';

/*
 * Types.
 */

export interface DisplayableRuleRecipe
  extends Omit<RuleRecipe, 'rule' | 'title' | 'description' | 'placeholders'> {
  title: string;
  description: string;

  type: string;
  url: string;
  icon: IconName;
  iconPalette: PaletteColorsEnum;
  imageUrl?: string;
  backgroundColor?: string;
  learnMoreUrl?: string;
}

/*
 * Styles.
 */

const StyledSmartRulesPill = styled(Pill)`
  display: grid;
  align-items: center;
  grid-template-columns: 8px 1fr;
  grid-gap: 2px;

  line-height: 9px;
`;

/*
 * Component.
 */

export const RuleLibraryRecipeCard: FC<RuleLibraryRecipeCardProps> = ({recipe}) => {
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);
  const missingFeatures = getMissingFeatures(recipe, findHasCompanyFeature);
  const reportRecipeClicked = () =>
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_ADD, {
      recipe_type: recipe.type,
      recipe_id: recipe.id,
      settings_type: 'team',
    });

  return (
    <UpsellOverlay
      hasUpsell={missingFeatures.length > 0}
      upsellUrl={UPSELL_URL}
      width={recipe.imageUrl ? '50%' : undefined}
    >
      <Link key={recipe.id} to={recipe.url} onClick={reportRecipeClicked}>
        <RuleLibraryCard
          key={recipe.id}
          title={recipe.title}
          icon={recipe.icon}
          iconPalette={recipe.iconPalette}
          imageUrl={recipe.imageUrl}
          backgroundColor={recipe.backgroundColor}
          pills={maybeRenderPills(recipe.requiredFeatures, missingFeatures)}
        >
          {recipe.description}
        </RuleLibraryCard>
      </Link>
    </UpsellOverlay>
  );
};

/*
 * Helpers.
 */

function getMissingFeatures(
  recipe: DisplayableRuleRecipe,
  findHasCompanyFeature: PredicateOf<CompanyFeaturesEnum>,
) {
  return recipe.requiredFeatures.filter((feature) => !hasCompanyFeature(feature, findHasCompanyFeature));
}

function hasCompanyFeature(
  feature: CompanyFeaturesEnum,
  findHasCompanyFeature: PredicateOf<CompanyFeaturesEnum>,
) {
  switch (feature) {
    case CompanyFeaturesEnum.UPGRADED_RULES:
      return (
        findHasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES) ||
        findHasCompanyFeature(CompanyFeaturesEnum.UPGRADED_RULES)
      );
    case CompanyFeaturesEnum.SLA_RULES:
      return (
        findHasCompanyFeature(CompanyFeaturesEnum.SLA_RULES) ||
        findHasCompanyFeature(CompanyFeaturesEnum.SLA_RULES_TRIAL)
      );
    default:
      return findHasCompanyFeature(feature);
  }
}

function maybeRenderPills(
  requiredFeatures: ReadonlyArray<CompanyFeaturesEnum>,
  missingFeatures: ReadonlyArray<CompanyFeaturesEnum>,
) {
  return [maybeRenderSmartRulePill(requiredFeatures), maybeRenderFeaturePill(missingFeatures)];
}

function maybeRenderSmartRulePill(requiredFeatures: ReadonlyArray<CompanyFeaturesEnum>) {
  if (!requiredFeatures.includes(CompanyFeaturesEnum.SMART_RULES)) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledSmartRulesPill
          key="smart-rule-pill"
          size={VisualSizesEnum.MEDIUM}
          color={theme.palette.teal.shade50}
          backgroundColor={theme.palette.teal.shade20}
        >
          <Icon name="sparkles" size={8} />
          <div>
            <Trans id="rule-library-smart-rule-pill">SMART RULE</Trans>
          </div>
        </StyledSmartRulesPill>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderFeaturePill(missingFeatures: ReadonlyArray<CompanyFeaturesEnum>) {
  if (missingFeatures.length === 0) {
    return null;
  }

  return (
    <Pill key="feature-pill" size={VisualSizesEnum.MEDIUM}>
      {missingFeatures.includes(CompanyFeaturesEnum.SMART_RULES) ? (
        <Trans id="rule-library-scale-pill">SCALE</Trans>
      ) : (
        <Trans id="rule-library-growth-pill">GROWTH</Trans>
      )}
    </Pill>
  );
}
